import { encodeAbiParameters, parseAbiParameters } from 'viem'
import { Call, isFailedCall } from '../../../utils/multicall'
import {
  decodeLensRows,
  getPositionLensFor,
  LENS_FAMILY,
  PositionLensAbi,
} from '../position-lens'

/**
 * GATED TO v6+ BY THE REGISTRY. The deployed v5 contract's Fraxlend rows carry
 * the converted amounts but NOT the `previewAddInterest` totals the converter
 * does its share math with (the §10 field ledger in USER_DATA_READ_PATH.md), so
 * `FRAXLEND` is withheld from every v5 entry and this returns `undefined`
 * everywhere until a v6 deployment declares it. The wiring below targets the
 * v6 blob (9 words per row, totals included) — written ahead of the deploy on
 * the Liquity precedent: the registry is the switch.
 */
export const usesFraxlendLens = (chainId: string): boolean =>
  getPositionLensFor(chainId, LENS_FAMILY.FRAXLEND) !== undefined

const isAddress = (v: unknown): v is string =>
  typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)

/**
 * ONE call instead of 4 per pair — and the numbers get MORE accurate, not
 * less: the lens converts shares through the pair's OWN previewing views
 * (`toBorrowAmount(shares, true, true)`), where the walk reproduces the same
 * math locally off `previewAddInterest`. Fraxlend is the worst live accrual
 * case in the book (56.7 bps understatement on its most active pair when the
 * preview is skipped), which is why the row carries both the answers and the
 * inputs.
 */
export const buildFraxlendLensCall = (
  chainId: string,
  pairs: { address?: string }[],
  account: string,
): Call[] => {
  const lens = getPositionLensFor(chainId, LENS_FAMILY.FRAXLEND)
  if (!lens) return []
  // Fail closed to the walk rather than encode a payload with a hole in it.
  if (pairs.some((p) => !isAddress(p.address))) return []
  const payload = encodeAbiParameters(parseAbiParameters('address[]'), [
    pairs.map((p) => p.address) as any,
  ])
  return [
    {
      address: lens,
      name: 'getUserData',
      params: [[{ family: LENS_FAMILY.FRAXLEND, payload }], account],
      abi: PositionLensAbi as any,
    },
  ]
}

/**
 * Rebuild the flat `data` array the per-pair walk would have produced, so the
 * converter's cursor arithmetic AND its share conversion run untouched.
 *
 * v6 blob layout (FraxlendLens.sol): `uint16 count`, rows of `uint16 index,
 * 9 x uint256` — collateral, borrow SHARES, lend SHARES, borrow AMOUNT, lend
 * AMOUNT, then previewAddInterest's totalAsset {amount, shares} and
 * totalBorrow {amount, shares}. No flags byte.
 *
 * The walk slot the converter reads totals from is the decoded
 * `previewAddInterest` tuple, where `[4]` is totalAsset and `[5]` totalBorrow —
 * reproduced here positionally so `getFraxlendUserDataConverter` cannot tell
 * the paths apart. The row's own converted amounts (words 3/4) are NOT fed to
 * the converter — it recomputes them from the totals with the identical
 * `VaultAccount.toAmount` rounding — but a caller can cross-check.
 *
 * A pair the lens did not emit is filled with ZEROS, which the converter reads
 * exactly as it reads the walk's zeros: no position. A pair whose totals read
 * zero WITH non-zero shares converts to shares-as-amount — a FLOOR, the same
 * degradation the walk has when `previewAddInterest` fails, and the reason
 * funding paths never read this surface.
 *
 * Returns `undefined` when the blob is unusable (including a 5-word v5 blob,
 * which fails the length check) — an incomplete read, never an empty position.
 */
export const rebuildFraxlendData = (
  blobs: unknown,
  pairCount: number,
): any[] | undefined => {
  if (isFailedCall(blobs) || !Array.isArray(blobs) || blobs.length !== 1) {
    return undefined
  }
  let decoded
  try {
    decoded = decodeLensRows(blobs[0] as string, 9, 0, 0)
  } catch {
    return undefined
  }

  const out: any[] = []
  for (let i = 0; i < pairCount; i++) {
    // coll, borrowShares, fToken balance, previewAddInterest tuple — the
    // walk's own order. The preview slots the converter reads are [4]/[5].
    out.push(0n, 0n, 0n, [0n, 0n, 0n, 0n, [0n, 0n], [0n, 0n]])
  }
  for (const r of decoded.rows) {
    if (r.index >= pairCount) return undefined
    const base = r.index * 4
    out[base] = r.words[0]
    out[base + 1] = r.words[1]
    out[base + 2] = r.words[2]
    out[base + 3] = [
      0n,
      0n,
      0n,
      0n,
      [r.words[5], r.words[6]],
      [r.words[7], r.words[8]],
    ]
  }
  return out
}
